// Live sibling reflow while DRAGGING is opt-in: hold Cmd (Meta) or Alt.
// Without the modifier the preview only marks the landing cell (siblings stay
// put) and the single authoritative reflow happens on drop — the common case
// is dropping into open space, and reflowing the whole board under every cell
// the pointer crosses reads as chaos. Shared by the internal and external
// drag hooks; RESIZE is deliberately not gated — pushing neighbors while a
// tile grows/shrinks is the grid's physicality centerpiece.
//
// dnd-kit's move events don't carry live modifier state, so a window
// keydown/keyup pair tracks it (any key event exposes metaKey/altKey) and
// re-derives the live preview mid-drag when the modifier toggles.

import * as React from 'react';

import type { ActiveMode, GridGestureCore } from './grid-gesture';

/** Reflow-modifier state on an activator (pointer/keyboard) event. */
export function reflowModifierHeld(event: unknown): boolean {
  const e = event as Partial<PointerEvent> | null | undefined;
  return Boolean(e && (e.metaKey || e.altKey));
}

/**
 * Keeps `heldRef` current while a `mode` gesture is live and re-derives the
 * preview (via `refresh`, from the last snapped target cell) when the
 * modifier toggles mid-drag. Listeners are permanent (cheap) and bail unless
 * the matching gesture is active.
 */
export function useReflowModifier(
  core: GridGestureCore,
  mode: ActiveMode,
  heldRef: React.MutableRefObject<boolean>,
  refresh: (cell: { x: number; y: number }) => void,
): void {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // `liveReflow` grids treat the modifier as always held — key events
      // must never toggle it off mid-drag.
      const held = core.liveReflow || e.metaKey || e.altKey;
      if (held === heldRef.current) return;
      heldRef.current = held;
      if (core.gestureRef.current?.mode !== mode) return;
      const cell = core.scratch.current.lastTarget;
      if (cell) refresh(cell);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
    };
  }, [core, mode, heldRef, refresh]);
}
